import StudentLayout from '../../../../layouts/student/StudentLayout';
import '../../../../assets/css/student/dashboard/Ticketsystem.css';
import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown, Send, Paperclip } from 'lucide-react';


const SupportTicketSystem = () => {
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [chatMessage, setChatMessage] = useState('');
  const [chatAttachment, setChatAttachment] = useState(null);
  const chatEndRef = useRef(null);
  const chatFileRef = useRef(null);

  // Dispute state
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [disputeData, setDisputeData] = useState({
    donor: '',
    issueType: '',
    description: '',
    file: null
  });
  const disputeFileRef = useRef(null);
  const ticketsPerPage = 5;

  const [tickets, setTickets] = useState([
    {
      id: '#1025',
      subject: 'Uniform not received',
      category: 'Uniform',
      status: 'pending',
      date: '24 Apr',
      message: 'I have not received my school uniform yet. When will it be delivered?',
      replies: [
        { from: 'admin', message: 'Your uniform is being processed. It will be delivered by this week.', date: '25 Apr', time: '10:30 AM' }
      ]
    },
    {
      id: '#1018',
      subject: 'Books delivered incomplete',
      category: 'Books',
      status: 'resolved',
      date: '18 Apr',
      message: 'I received the books but some subjects are missing from the package.',
      replies: [
        { from: 'admin', message: 'The missing books have been sent to your address. You will receive them soon.', date: '19 Apr', time: '2:15 PM' }
      ]
    },
    {
      id: '#1009',
      subject: 'Wrong uniform size',
      category: 'Uniform',
      status: 'rejected',
      date: '12 Apr',
      message: 'The uniform I received is the wrong size. I need a larger size.',
      replies: [
        { from: 'admin', message: 'Please provide the correct size details. We will arrange an exchange.', date: '13 Apr', time: '11:00 AM' }
      ]
    },
    {
      id: '#1005',
      subject: 'Fee payment confirmation',
      category: 'Fee',
      status: 'in-progress',
      date: '8 Apr',
      message: 'I have paid my fee but have not received any confirmation yet.',
      replies: [
        { from: 'admin', message: 'We are checking the payment records. You will receive an update soon.', date: '9 Apr', time: '9:45 AM' }
      ]
    },
    {
      id: '#1002',
      subject: 'Books quality issue',
      category: 'Books',
      status: 'pending',
      date: '5 Apr',
      message: 'Some of the books I received have damaged pages and covers.',
      replies: []
    },
    {
      id: '#0998',
      subject: 'Uniform delivery delayed',
      category: 'Uniform',
      status: 'resolved',
      date: '2 Apr',
      message: 'My uniform has not been delivered yet even after 2 weeks.',
      replies: [
        { from: 'admin', message: 'Your uniform has been delivered. Please check and confirm receipt.', date: '3 Apr', time: '4:00 PM' }
      ]
    },
    {
      id: '#0995',
      subject: 'Fee receipt not generated',
      category: 'Fee',
      status: 'in-progress',
      date: '30 Mar',
      message: 'I need a receipt for the fee I paid last month.',
      replies: []
    }
  ]);

  useEffect(() => {
    if (showDetailModal) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedTicket?.replies, showDetailModal]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'st-badge-pending', text: 'Pending' },
      'in-progress': { class: 'st-badge-progress', text: 'In Progress' },
      resolved: { class: 'st-badge-resolved', text: 'Resolved' },
      rejected: { class: 'st-badge-rejected', text: 'Rejected' }
    };
    const config = statusConfig[status];
    return <span className={`st-status-badge ${config.class}`}>{config.text}</span>;
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowDetailModal(true);
    setChatMessage('');
    setChatAttachment(null);
    setShowDisputeForm(false);
    setDisputeData({ donor: '', issueType: '', description: '', file: null });
  };

  const handleDisputeSubmit = () => {
    if (!disputeData.donor || !disputeData.issueType || !disputeData.description) {
      alert('Please fill all required fields');
      return;
    }
    alert('Dispute submitted successfully!');
    setShowDisputeForm(false);
    setDisputeData({ donor: '', issueType: '', description: '', file: null });
    if (disputeFileRef.current) disputeFileRef.current.value = '';
  };

  const handleSendMessage = () => {
    if (!chatMessage.trim() && !chatAttachment) return;
    const now = new Date();
    const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const dateStr = now.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    const newReply = {
      from: 'student',
      message: chatMessage.trim(),
      date: dateStr,
      time: timeStr,
      attachment: chatAttachment ? chatAttachment.name : null
    };
    const updatedTickets = tickets.map(t =>
      t.id === selectedTicket.id ? { ...t, replies: [...t.replies, newReply] } : t
    );
    setTickets(updatedTickets);
    setSelectedTicket(prev => ({ ...prev, replies: [...prev.replies, newReply] }));
    setChatMessage('');
    setChatAttachment(null);
    if (chatFileRef.current) chatFileRef.current.value = '';
  };

  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    if (activeTab === 'all') return true;
    if (activeTab === 'pending') return ticket.status === 'pending';
    if (activeTab === 'in-progress') return ticket.status === 'in-progress';
    if (activeTab === 'resolved') return ticket.status === 'resolved';
    return true;
  });

  const indexOfLastTicket = currentPage * ticketsPerPage;
  const indexOfFirstTicket = indexOfLastTicket - ticketsPerPage;
  const currentTickets = filteredTickets.slice(indexOfFirstTicket, indexOfLastTicket);
  const totalPages = Math.ceil(filteredTickets.length / ticketsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <StudentLayout>
      <div className="st-container">
        {/* ── Header (Raise Ticket button removed) ── */}
        <div className="st-header">
          <div className="st-header-content">
            <div>
              <h1 className="st-title">Support Tickets</h1>
              <p className="st-subtitle">Submit & Track Your Donation Issues</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="st-tabs">
          <button className={`st-tab ${activeTab === 'all' ? 'st-tab-active' : ''}`} onClick={() => { setActiveTab('all'); setCurrentPage(1); }}>All Tickets</button>
          <button className={`st-tab ${activeTab === 'pending' ? 'st-tab-active' : ''}`} onClick={() => { setActiveTab('pending'); setCurrentPage(1); }}>
            <span className="st-tab-icon st-icon-pending">⊕</span> Pending
          </button>
          <button className={`st-tab ${activeTab === 'in-progress' ? 'st-tab-active' : ''}`} onClick={() => { setActiveTab('in-progress'); setCurrentPage(1); }}>
            <span className="st-tab-icon st-icon-progress">✓</span> In Progress
          </button>
          <button className={`st-tab ${activeTab === 'resolved' ? 'st-tab-active' : ''}`} onClick={() => { setActiveTab('resolved'); setCurrentPage(1); }}>
            <span className="st-tab-icon st-icon-resolved">✓</span> Resolved
          </button>
        </div>

        {/* Table */}
        <div className="st-table-container">
          <table className="st-table">
            <thead>
              <tr>
                <th>Ticket ID</th><th>Subject</th><th>Category</th><th>Status</th><th>Date</th><th>Action</th>
              </tr>
            </thead>
            <tbody>
              {currentTickets.map((ticket) => (
                <tr key={ticket.id}>
                  <td className="st-ticket-id">{ticket.id}</td>
                  <td>{ticket.subject}</td>
                  <td>{ticket.category}</td>
                  <td>{getStatusBadge(ticket.status)}</td>
                  <td>{ticket.date}</td>
                  <td><button className="st-view-btn" onClick={() => handleViewTicket(ticket)}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="st-pagination">
          <button className="st-page-btn" onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
          {[...Array(totalPages)].map((_, index) => (
            <button key={index} className={`st-page-num ${currentPage === index + 1 ? 'st-page-active' : ''}`} onClick={() => paginate(index + 1)}>{index + 1}</button>
          ))}
          <button className="st-page-btn" onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
        </div>

        {/* Detail Modal with Chat */}
        {showDetailModal && selectedTicket && (
          <div className="st-modal-overlay" onClick={() => setShowDetailModal(false)}>
            <div className="st-modal-detail" onClick={(e) => e.stopPropagation()}>
              <div className="st-modal-header">
                <h2>Ticket Details - {selectedTicket.id}</h2>
                <button className="st-close-btn" onClick={() => setShowDetailModal(false)}><X size={24} /></button>
              </div>

              <div className="st-modal-body">
                <div className="st-ticket-metadata">
                  <div className="st-metadata-grid">
                    <div className="st-metadata-item">
                      <label className="st-metadata-label">Ticket ID</label>
                      <span className="st-metadata-value st-ticket-id-value">{selectedTicket.id}</span>
                    </div>
                    <div className="st-metadata-item">
                      <label className="st-metadata-label">Date Submitted</label>
                      <span className="st-metadata-value">{selectedTicket.date}</span>
                    </div>
                    <div className="st-metadata-item">
                      <label className="st-metadata-label">Category</label>
                      <span className="st-metadata-value st-category-value">{selectedTicket.category}</span>
                    </div>
                    <div className="st-metadata-item">
                      <label className="st-metadata-label">Current Status</label>
                      <div className="st-metadata-value">{getStatusBadge(selectedTicket.status)}</div>
                    </div>
                  </div>
                  <div className="st-subject-section">
                    <label className="st-metadata-label">Subject</label>
                    <p className="st-subject-text">{selectedTicket.subject}</p>
                  </div>
                </div>

                {/* Conversation Thread */}
                <div className="st-conversation-section">
                  <h3 className="st-conversation-title">Conversation Thread</h3>
                  <div className="st-conversation-timeline">

                    <div className="st-timeline-item">
                      <div className="st-timeline-badge st-timeline-badge-student">S</div>
                      <div className="st-timeline-content">
                        <div className="st-message-card st-message-student-card">
                          <div className="st-message-meta">
                            <span className="st-sender-name">You (Student)</span>
                            <span className="st-message-timestamp">{selectedTicket.date}</span>
                          </div>
                          <div className="st-message-content">{selectedTicket.message}</div>
                        </div>
                      </div>
                    </div>

                    {selectedTicket.replies.length > 0 ? (
                      selectedTicket.replies.map((reply, index) => {
                        const isStudent = reply.from === 'student';
                        return (
                          <div key={index} className="st-timeline-item">
                            <div className={`st-timeline-badge ${isStudent ? 'st-timeline-badge-student' : 'st-timeline-badge-admin'}`}>
                              {isStudent ? 'S' : 'A'}
                            </div>
                            <div className="st-timeline-content">
                              <div className={`st-message-card ${isStudent ? 'st-message-student-card' : 'st-message-admin-card'}`}>
                                <div className="st-message-meta">
                                  <span className="st-sender-name">{isStudent ? 'You (Student)' : 'Support Team'}</span>
                                  <span className="st-message-timestamp">{reply.date} at {reply.time}</span>
                                </div>
                                {reply.message && <div className="st-message-content">{reply.message}</div>}
                                {reply.attachment && (
                                  <div className="st-chat-attachment-badge">
                                    <Paperclip size={13} /> {reply.attachment}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="st-no-replies">
                        <p>No responses yet. Our support team will respond shortly.</p>
                      </div>
                    )}

                    <div ref={chatEndRef} />
                  </div>
                </div>

                {/* Chat Input Box */}
                <div className="st-chat-input-area">
                  {chatAttachment && (
                    <div className="st-chat-attachment-preview">
                      <Paperclip size={14} />
                      <span>{chatAttachment.name}</span>
                      <button className="st-chat-remove-attachment" onClick={() => { setChatAttachment(null); if (chatFileRef.current) chatFileRef.current.value = ''; }}>
                        <X size={13} />
                      </button>
                    </div>
                  )}
                  <div className="st-chat-input-row">
                    <button className="st-chat-attach-btn" title="Attach file" onClick={() => chatFileRef.current?.click()}>
                      <Paperclip size={18} />
                    </button>
                    <input ref={chatFileRef} type="file" style={{ display: 'none' }} onChange={(e) => setChatAttachment(e.target.files[0] || null)} />
                    <textarea
                      className="st-chat-textarea"
                      rows={1}
                      placeholder="Type your message..."
                      value={chatMessage}
                      onChange={(e) => setChatMessage(e.target.value)}
                      onKeyDown={handleChatKeyDown}
                    />
                    <button className="st-chat-send-btn" onClick={handleSendMessage} disabled={!chatMessage.trim() && !chatAttachment}>
                      <Send size={18} />
                    </button>
                  </div>
                  <p className="st-chat-hint">Press Enter to send &middot; Shift+Enter for new line</p>
                </div>

                {/* Raise Dispute Button */}
                <div className="st-raise-dispute-row">
                  <button className="st-dispute-btn" onClick={() => setShowDisputeForm(true)}>
                    ⚠ Raise Dispute
                  </button>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* Dispute Modal */}
        {showDisputeForm && selectedTicket && (
          <div className="st-modal-overlay" style={{ zIndex: 1100 }} onClick={() => setShowDisputeForm(false)}>
            <div className="st-modal-dispute" onClick={(e) => e.stopPropagation()}>
              <div className="st-modal-header st-dispute-modal-header">
                <div>
                  <h2>⚠ Raise a Dispute</h2>
                  <p className="st-dispute-modal-sub">Ticket {selectedTicket.id} — {selectedTicket.subject}</p>
                </div>
                <button className="st-close-btn" onClick={() => {
                  setShowDisputeForm(false);
                  setDisputeData({ donor: '', issueType: '', description: '', file: null });
                  if (disputeFileRef.current) disputeFileRef.current.value = '';
                }}><X size={24} /></button>
              </div>

              <div className="st-modal-body">
                <div className="st-dispute-grid">
                  <div className="st-form-group">
                    <label>Donor / NGO <span className="st-required">*</span></label>
                    <div className="st-select-wrapper">
                      <select
                        className="st-select"
                        value={disputeData.donor}
                        onChange={(e) => setDisputeData({ ...disputeData, donor: e.target.value })}
                      >
                        <option value="">Select Donor / NGO</option>
                        <option value="ali">Ali</option>
                        <option value="ahmed">Ahmed</option>
                      </select>
                      <ChevronDown className="st-select-icon" size={18} />
                    </div>
                  </div>

                  <div className="st-form-group">
                    <label>Issue Type <span className="st-required">*</span></label>
                    <div className="st-select-wrapper">
                      <select
                        className="st-select"
                        value={disputeData.issueType}
                        onChange={(e) => setDisputeData({ ...disputeData, issueType: e.target.value })}
                      >
                        <option value="">Select Issue Type</option>
                        <option value="fraud">Fraud / Misuse</option>
                        <option value="late">Late Donation / Missing</option>
                        <option value="misbehavior">Misbehavior / Harassment</option>
                        <option value="other">Other</option>
                      </select>
                      <ChevronDown className="st-select-icon" size={18} />
                    </div>
                  </div>
                </div>

                <div className="st-form-group">
                  <label>Description <span className="st-required">*</span></label>
                  <textarea
                    className="st-textarea"
                    rows={4}
                    placeholder="Describe your dispute in detail..."
                    value={disputeData.description}
                    onChange={(e) => setDisputeData({ ...disputeData, description: e.target.value })}
                  />
                </div>

                <div className="st-form-group">
                  <label>Upload Evidence <span className="st-optional">(Optional)</span></label>
                  <div className="st-file-input">
                    <input
                      ref={disputeFileRef}
                      type="file"
                      id="disputeFileInput"
                      className="st-file-hidden"
                      onChange={(e) => setDisputeData({ ...disputeData, file: e.target.files[0] || null })}
                    />
                    <label htmlFor="disputeFileInput" className="st-file-label">Choose File</label>
                    <span className="st-file-text">
                      {disputeData.file ? disputeData.file.name : 'No file chosen'}
                    </span>
                  </div>
                </div>

                <div className="st-modal-actions">
                  <button className="st-submit-dispute-btn" onClick={handleDisputeSubmit}>
                    Submit Dispute
                  </button>
                  <button className="st-cancel-btn" onClick={() => {
                    setShowDisputeForm(false);
                    setDisputeData({ donor: '', issueType: '', description: '', file: null });
                    if (disputeFileRef.current) disputeFileRef.current.value = '';
                  }}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </StudentLayout>
  );
};

export default SupportTicketSystem;